// Finance dashboard (wave 3, W2 look): the default tab of Transaction logs.
//
// A pure read of /transactionStats (the per-day totals bumpTransactionStat
// keeps in the Cloud Functions) through the shared 5-minute cache: one
// ranged read for the last 90 days and one for the year to date, whatever
// the number of wallet entries behind them. Days are Addis Ababa days, the
// same keys the server writes.
//
//   Platform commission = GMV − driver earnings (the headline)
//   GMV                 = Σ Debits of the booking sources
//   Driver earnings     = Σ Credits of the booking sources
//   Top-up inflow       = Credits of top_up
//   Withdrawals         = Σ (Credit + Debit) of withdraw
// The "How it's calculated" tab explains every figure.
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import DashboardCard from '../../components/DashboardCard';
import { EmptyState } from '../../components/feedback';
import { tokens as c } from '../../theme';
import { addisDateStamp } from '../../common/exportUtils';
import { DropChip, IconCircle, StatusChip } from '../../components/modern';
import {
  SOURCE_BUCKETS, BOOKING_SOURCES,
  fetchStatsCached, invalidateStatsCache,
} from './sourceBuckets';

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n) => (n < 10 ? `0${n}` : String(n));

// Addis Ababa date keys ('YYYY-MM-DD', as /transactionStats is keyed).
const keyDaysAgo = (now, days) => addisDateStamp(now - days * DAY_MS);
const partsOf = (key) => key.split('-').map(Number);
// "27 Oct" for a date key (chart axis, top days).
const shortDay = (key) => {
  const [, m, d] = partsOf(key);
  return `${d} ${MONTHS[m - 1]}`;
};

function Section({ icon, hue, title, sub, action, children, sx }) {
  return (
    <Paper component="section" sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', ...(sx || {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, mb: 1.5 }}>
        <IconCircle icon={icon} hue={hue} size={34} />
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4, color: c.INK }}>
            {title}
          </Typography>
          {sub ? <Typography sx={{ fontSize: 13, color: c.TEXT_3, lineHeight: 1.45 }}>{sub}</Typography> : null}
        </Box>
        {action || null}
      </Box>
      {children}
    </Paper>
  );
}

const FinanceDashboard = ({ settings, dispatch }) => {
  const { t, i18n } = useTranslation();
  const symbol = settings?.symbol || '';
  const swipeSymbol = settings?.swipe_symbol;
  const fmt = (val) => {
    const n = parseFloat(val) || 0;
    const s = n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : n.toFixed(2);
    return swipeSymbol ? `${s} ${symbol}` : `${symbol} ${s}`;
  };
  const fmtCompact = (val) => {
    const n = Math.abs(parseFloat(val) || 0);
    if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
    return n.toFixed(0);
  };
  const sourceLabel = (key) => (SOURCE_BUCKETS[key] ? t(`tx_source_${key}`, { defaultValue: SOURCE_BUCKETS[key].label }) : key);

  // 90 days of stats covers every figure AND the trend chart; the year to
  // date beyond 90 days comes from a second (cached) read.
  const [stats, setStats] = useState({ byDay: {}, bySource: {}, byType: {}, total: 0, days: 0, loading: true });
  const [ytdStats, setYtdStats] = useState({ total: 0, byType: {}, bySource: {}, loading: true });
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [mixSourceFilter, setMixSourceFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    const now = Date.now();
    const end = keyDaysAgo(now, 0);
    const start90 = keyDaysAgo(now, 90);
    const startYtd = `${partsOf(end)[0]}-01-01`;

    setStats((prev) => Object.assign({}, prev, { loading: true }));
    setYtdStats((prev) => Object.assign({}, prev, { loading: true }));

    const opts = { force: refreshTick > 0 };

    Promise.all([
      fetchStatsCached(dispatch, start90, end, opts),
      fetchStatsCached(dispatch, startYtd, end, opts),
    ]).then(([r90, rytd]) => {
      if (cancelled) return;
      setStats(Object.assign({}, r90, { loading: false }));
      setYtdStats(Object.assign({}, rytd, { loading: false }));
      setLastLoadedAt(Date.now());
    }).catch(() => {
      if (cancelled) return;
      setStats((s) => Object.assign({}, s, { loading: false }));
      setYtdStats((s) => Object.assign({}, s, { loading: false }));
    });

    return () => { cancelled = true; };
  }, [dispatch, refreshTick]);

  // Sum stats.byDay between two date keys inclusive.
  const sumRange = (startKey, endKey) => {
    const acc = {
      volume: 0, credit: 0, debit: 0,
      gmv: 0, driverEarnings: 0, commission: 0,
      topUp: 0, withdraw: 0,
      adminCredit: 0, tips: 0, cancellationFees: 0,
    };
    Object.keys(stats.byDay).forEach((dateKey) => {
      if (dateKey < startKey || dateKey > endKey) return;
      const day = stats.byDay[dateKey] || {};
      Object.keys(day).forEach((source) => {
        const sd = day[source] || {};
        const cr = Number(sd.Credit) || 0;
        const d = Number(sd.Debit) || 0;
        acc.credit += cr;
        acc.debit += d;
        acc.volume += (cr + d);
        if (BOOKING_SOURCES.indexOf(source) >= 0) {
          acc.gmv += d;
          acc.driverEarnings += cr;
        }
        if (source === 'top_up') acc.topUp += cr;
        if (source === 'withdraw') acc.withdraw += (cr + d);
        if (source === 'admin_credit') acc.adminCredit += cr;
        if (source === 'tip') acc.tips += cr;
        if (source === 'cancellation_fee') acc.cancellationFees += (cr + d);
      });
    });
    acc.commission = acc.gmv - acc.driverEarnings;
    return acc;
  };

  const now = Date.now();
  const todayKey = keyDaysAgo(now, 0);
  const yesterdayKey = keyDaysAgo(now, 1);
  const last7Start = keyDaysAgo(now, 6);
  const prev7Start = keyDaysAgo(now, 13);
  const prev7End = keyDaysAgo(now, 7);
  const [ty, tm] = partsOf(todayKey);
  const mtdStart = `${ty}-${pad2(tm)}-01`;
  const lmYear = tm === 1 ? ty - 1 : ty;
  const lmMonth = tm === 1 ? 12 : tm - 1;
  const lastMonthStart = `${lmYear}-${pad2(lmMonth)}-01`;
  const lastMonthEnd = `${lmYear}-${pad2(lmMonth)}-${pad2(new Date(Date.UTC(lmYear, lmMonth, 0)).getUTCDate())}`;

  const todayStats = sumRange(todayKey, todayKey);
  const yesterdayStats = sumRange(yesterdayKey, yesterdayKey);
  const last7Stats = sumRange(last7Start, todayKey);
  const prev7Stats = sumRange(prev7Start, prev7End);
  const mtdStats_ = sumRange(mtdStart, todayKey);
  const lastMonthStats = sumRange(lastMonthStart, lastMonthEnd);

  const ytdMetrics = (() => {
    let gmv = 0;
    let de = 0;
    Object.keys(ytdStats.bySource || {}).forEach((source) => {
      if (BOOKING_SOURCES.indexOf(source) >= 0) {
        gmv += Number(ytdStats.bySource[source].Debit) || 0;
        de += Number(ytdStats.bySource[source].Credit) || 0;
      }
    });
    return { gmv, driverEarnings: de, commission: gmv - de };
  })();

  const pctDelta = (curr, prev) => {
    if (!prev || prev === 0) return curr > 0 ? 999 : 0;
    const pct = ((curr - prev) / Math.abs(prev)) * 100;
    if (pct > 999) return 999;
    if (pct < -999) return -999;
    return pct;
  };

  // The trend line under a figure: an arrow and "+12.5% vs yesterday".
  const deltaLine = (delta, deltaLabel) => {
    if (delta === null || delta === undefined) return null;
    const positive = delta > 0;
    const negative = delta < 0;
    const color = positive ? c.SUCCESS_TEXT : negative ? c.DANGER_TEXT : c.TEXT_3;
    const text =
      delta === 999
        ? t('fin_delta_new', { defaultValue: '↑ new' })
        : delta === -999
          ? t('fin_delta_all', { defaultValue: '↓ all' })
          : `${positive ? '+' : ''}${delta.toFixed(1)}%`;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {positive ? <TrendingUpRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} /> : null}
        {negative ? <TrendingDownRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} /> : null}
        {text}
        {deltaLabel ? <Box component="span" sx={{ color: c.TEXT_3, fontWeight: 400 }}>{deltaLabel}</Box> : null}
      </Box>
    );
  };

  // ── 30-day trend chart data ───────────────────────────────────────
  const trendData = useMemo(() => {
    const data = [];
    const base = Date.now();
    for (let i = 29; i >= 0; i--) {
      const key = keyDaysAgo(base, i);
      const row = { date: shortDay(key), _key: key, total: 0 };
      const day = stats.byDay[key] || {};
      Object.keys(SOURCE_BUCKETS).forEach((s) => {
        const sd = day[s] || {};
        const v = (Number(sd.Credit) || 0) + (Number(sd.Debit) || 0);
        row[s] = v;
        row.total += v;
      });
      data.push(row);
    }
    return data;
  }, [stats.byDay]);

  const chartSources = useMemo(() => {
    const totals = {};
    Object.keys(SOURCE_BUCKETS).forEach((s) => {
      totals[s] = trendData.reduce((sum, r) => sum + (r[s] || 0), 0);
    });
    return Object.keys(totals)
      .filter((s) => totals[s] > 0)
      .sort((a, b) => totals[b] - totals[a]);
  }, [trendData]);

  // ── Source mix donut (this month), filterable by source ────────────
  const mixData = useMemo(() => {
    const out = [];
    if (mixSourceFilter === 'all') {
      Object.keys(SOURCE_BUCKETS).forEach((s) => {
        const v = Object.keys(stats.byDay).reduce((acc, dateKey) => {
          if (dateKey < mtdStart || dateKey > todayKey) return acc;
          const sd = (stats.byDay[dateKey] || {})[s] || {};
          return acc + (Number(sd.Credit) || 0) + (Number(sd.Debit) || 0);
        }, 0);
        if (v > 0) out.push({ name: sourceLabel(s), value: v, color: SOURCE_BUCKETS[s].color, key: s });
      });
    } else {
      let credit = 0;
      let debit = 0;
      Object.keys(stats.byDay).forEach((dateKey) => {
        if (dateKey < mtdStart || dateKey > todayKey) return;
        const sd = (stats.byDay[dateKey] || {})[mixSourceFilter] || {};
        credit += Number(sd.Credit) || 0;
        debit += Number(sd.Debit) || 0;
      });
      if (credit > 0) out.push({ name: t('fin_credit_inflow', { defaultValue: 'Credit (inflow)' }), value: credit, color: c.SUCCESS_TEXT, key: 'Credit' });
      if (debit > 0) out.push({ name: t('fin_debit_outflow', { defaultValue: 'Debit (outflow)' }), value: debit, color: c.DANGER_TEXT, key: 'Debit' });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats.byDay, mtdStart, todayKey, mixSourceFilter, i18n.language]);
  const mixTotal = mixData.reduce((s, r) => s + r.value, 0);

  // ── Top platform commission days, last 30 ─────────────────────────
  const topCommissionDays = useMemo(() => {
    const days = [];
    const base = Date.now();
    const start = keyDaysAgo(base, 29);
    const end = keyDaysAgo(base, 0);
    Object.keys(stats.byDay).forEach((dateKey) => {
      if (dateKey < start || dateKey > end) return;
      let gmv = 0;
      let de = 0;
      const day = stats.byDay[dateKey] || {};
      BOOKING_SOURCES.forEach((s) => {
        const sd = day[s] || {};
        gmv += Number(sd.Debit) || 0;
        de += Number(sd.Credit) || 0;
      });
      const commission = gmv - de;
      if (commission > 0) {
        days.push({ date: shortDay(dateKey), _key: dateKey, commission, gmv });
      }
    });
    days.sort((a, b) => b.commission - a.commission);
    const top = days.slice(0, 5);
    if (days.length < 3) return top.map((r) => Object.assign({}, r, { isSpike: false, pctVsAvg: 0 }));
    const all = days.map((d) => d.commission);
    const mean = all.reduce((a, b) => a + b, 0) / all.length;
    const variance = all.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / all.length;
    const std = Math.sqrt(variance);
    return top.map((r) => Object.assign({}, r, {
      isSpike: r.commission > mean + 1.5 * std,
      pctVsAvg: mean > 0 ? ((r.commission - mean) / mean) * 100 : 0,
    }));
  }, [stats.byDay]);

  const TRENDED_KPIS = stats.days > 0;

  const lastLoadedLabel = (() => {
    if (!lastLoadedAt) return null;
    const sec = Math.floor((Date.now() - lastLoadedAt) / 1000);
    if (sec < 60) return t('time_just_now', { defaultValue: 'just now' });
    const min = Math.floor(sec / 60);
    if (min < 60) return t('fin_updated_min', { n: min, defaultValue: '{{n}} min ago' });
    return t('fin_updated_hours', { h: Math.floor(min / 60), m: min % 60, defaultValue: '{{h}}h {{m}}m ago' });
  })();

  const vsYesterday = t('fin_vs_yesterday', { defaultValue: 'vs yesterday' });
  const vsPrev7 = t('fin_vs_prev7', { defaultValue: 'vs previous 7 days' });
  const vsLastMonth = t('fin_vs_last_month', { defaultValue: 'vs last month' });
  const loading = stats.loading && Object.keys(stats.byDay).length === 0;

  const tiles1 = [
    { key: 'c-today', icon: <AccountBalanceOutlinedIcon />, hue: 'accent', title: t('fin_commission_today', { defaultValue: 'Platform commission · Today' }), value: todayStats.commission, caption: deltaLine(TRENDED_KPIS ? pctDelta(todayStats.commission, yesterdayStats.commission) : null, vsYesterday) },
    { key: 'c-week', icon: <AccountBalanceOutlinedIcon />, hue: 'accent', title: t('fin_commission_week', { defaultValue: 'Platform commission · This week' }), value: last7Stats.commission, caption: deltaLine(TRENDED_KPIS ? pctDelta(last7Stats.commission, prev7Stats.commission) : null, vsPrev7) },
    { key: 'c-month', icon: <AccountBalanceOutlinedIcon />, hue: 'accent', title: t('fin_commission_month', { defaultValue: 'Platform commission · This month' }), value: mtdStats_.commission, caption: deltaLine(TRENDED_KPIS && lastMonthStats.commission > 0 ? pctDelta(mtdStats_.commission, lastMonthStats.commission) : null, vsLastMonth) },
    { key: 'c-ytd', icon: <AccountBalanceOutlinedIcon />, hue: 'accent', title: t('fin_commission_ytd', { defaultValue: 'Platform commission · Year to date' }), value: ytdMetrics.commission, caption: ytdStats.loading ? t('loading', { defaultValue: 'Loading…' }) : t('fin_active_days', { n: ytdStats.days, defaultValue: '{{n}} active days' }) },
  ];
  const tiles2 = [
    { key: 'gmv', icon: <DirectionsCarOutlinedIcon />, hue: 'info', title: t('fin_gmv_week', { defaultValue: 'GMV · This week' }), value: last7Stats.gmv, caption: deltaLine(TRENDED_KPIS ? pctDelta(last7Stats.gmv, prev7Stats.gmv) : null, vsPrev7) },
    { key: 'earn', icon: <DirectionsCarOutlinedIcon />, hue: 'info', title: t('fin_driver_earnings_week', { defaultValue: 'Driver earnings · This week' }), value: last7Stats.driverEarnings, caption: deltaLine(TRENDED_KPIS ? pctDelta(last7Stats.driverEarnings, prev7Stats.driverEarnings) : null, vsPrev7) },
    { key: 'topup', icon: <LocalAtmOutlinedIcon />, hue: 'success', title: t('fin_topup_week', { defaultValue: 'Top-up inflow · This week' }), value: last7Stats.topUp, caption: deltaLine(TRENDED_KPIS ? pctDelta(last7Stats.topUp, prev7Stats.topUp) : null, vsPrev7) },
    { key: 'withdraw', icon: <PaymentsOutlinedIcon />, hue: 'amber', title: t('fin_withdrawals_week', { defaultValue: 'Withdrawals · This week' }), value: last7Stats.withdraw, caption: deltaLine(TRENDED_KPIS ? pctDelta(last7Stats.withdraw, prev7Stats.withdraw) : null, vsPrev7) },
  ];
  const tileGrid = { display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } };

  const mixOptions = [
    { value: 'all', label: t('all_sources', { defaultValue: 'All sources' }) },
    ...Object.keys(SOURCE_BUCKETS).map((key) => ({ value: key, label: sourceLabel(key) })),
  ];
  const mixTitle =
    mixSourceFilter === 'all'
      ? t('fin_mix_title', { defaultValue: 'Source mix · This month' })
      : t('fin_mix_title_source', { source: sourceLabel(mixSourceFilter), defaultValue: '{{source}} · This month' });

  const axisTick = { fontSize: 11, fill: c.TEXT_3 };
  const tooltipStyle = { fontSize: 12, borderRadius: 8, border: `1px solid ${c.OUTLINE_STRONG}`, color: c.INK };

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.5 }}>
        {lastLoadedAt ? (
          <Typography variant="body2" sx={{ color: c.TEXT_3 }}>
            {t('fin_updated', { when: lastLoadedLabel, defaultValue: 'Updated {{when}} · kept for 5 min' })}
          </Typography>
        ) : null}
        <Button
          variant="outlined"
          color="neutral"
          size="small"
          startIcon={<RefreshRoundedIcon />}
          disabled={stats.loading}
          onClick={() => {
            invalidateStatsCache();
            setRefreshTick((n) => n + 1);
          }}
        >
          {stats.loading ? t('fin_refreshing', { defaultValue: 'Refreshing…' }) : t('fin_refresh_now', { defaultValue: 'Refresh now' })}
        </Button>
      </Box>

      {/* Row 1: platform commission (the headline) */}
      <Box sx={tileGrid}>
        {tiles1.map((tile) => (
          <DashboardCard key={tile.key} icon={tile.icon} hue={tile.hue} title={tile.title} value={fmt(tile.value)} caption={tile.caption} loading={stats.loading} />
        ))}
      </Box>

      {/* Row 2: GMV, driver earnings and cash flow */}
      <Box sx={tileGrid}>
        {tiles2.map((tile) => (
          <DashboardCard key={tile.key} icon={tile.icon} hue={tile.hue} title={tile.title} value={fmt(tile.value)} caption={tile.caption} loading={stats.loading} />
        ))}
      </Box>

      {/* Row 3: 30-day trend */}
      <Section
        icon={<InsightsOutlinedIcon />}
        hue="info"
        title={t('fin_trend_title', { defaultValue: '30-day activity' })}
        sub={t('fin_trend_sub', { defaultValue: 'Daily volume, stacked by source' })}
      >
        <Box sx={{ width: '100%', height: 320 }}>
          {loading ? null : (
            <ResponsiveContainer>
              <AreaChart data={trendData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.OUTLINE} />
                <XAxis dataKey="date" tick={axisTick} interval="preserveStartEnd" stroke={c.OUTLINE_STRONG} />
                <YAxis tick={axisTick} tickFormatter={fmtCompact} stroke={c.OUTLINE_STRONG} />
                <RTooltip formatter={(value, name) => [fmt(value), name]} contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12, color: c.INK_2 }} />
                {chartSources.map((s) => (
                  <Area
                    key={s}
                    type="monotone"
                    dataKey={s}
                    stackId="1"
                    name={sourceLabel(s)}
                    stroke={SOURCE_BUCKETS[s].color}
                    fill={SOURCE_BUCKETS[s].color}
                    fillOpacity={0.7}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Section>

      {/* Row 4: source mix and the top commission days */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '5fr 7fr' } }}>
        <Section
          icon={<DonutLargeOutlinedIcon />}
          hue="accent"
          title={mixTitle}
          action={<DropChip label={t('source', { defaultValue: 'Source' })} value={mixSourceFilter} allValue="all" options={mixOptions} onChange={setMixSourceFilter} />}
        >
          <Box sx={{ width: '100%', height: 280 }}>
            {mixData.length === 0 ? (
              <EmptyState
                compact
                icon={<DonutLargeOutlinedIcon />}
                title={
                  mixSourceFilter === 'all'
                    ? t('fin_mix_empty', { defaultValue: 'No activity yet this month' })
                    : t('fin_mix_empty_source', { source: sourceLabel(mixSourceFilter), defaultValue: 'No {{source}} activity this month' })
                }
              />
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={mixData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={2}
                    label={({ value }) => `${((value / mixTotal) * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {mixData.map((entry) => (
                      <Cell key={entry.key} fill={entry.color} />
                    ))}
                  </Pie>
                  <RTooltip formatter={(value) => fmt(value)} contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12, color: c.INK_2 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Section>

        <Section
          icon={<EmojiEventsOutlinedIcon />}
          hue="amber"
          title={t('fin_top_days_title', { defaultValue: 'Top platform commission days · Last 30' })}
          sub={t('fin_top_days_sub', { defaultValue: 'Ranked by commission · spike = more than 1.5σ above the mean' })}
        >
          {topCommissionDays.length === 0 ? (
            <EmptyState compact icon={<EmojiEventsOutlinedIcon />} title={t('fin_top_days_empty', { defaultValue: 'No commission in the last 30 days.' })} />
          ) : (
            <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {topCommissionDays.map((row, idx) => (
                <Box
                  component="li"
                  key={row._key}
                  sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, py: 1.25, borderTop: idx ? `1px solid ${c.OUTLINE}` : 0 }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      flex: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                      bgcolor: idx === 0 ? c.AMBER_TINT : c.SURFACE_ALT,
                      color: idx === 0 ? c.AMBER_TEXT : c.INK_2,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: '1 1 140px' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: c.INK }}>{row.date}</Typography>
                    <Typography sx={{ fontSize: 13, color: c.TEXT_3, fontVariantNumeric: 'tabular-nums' }}>
                      {t('fin_gmv_line', { amount: fmt(row.gmv), defaultValue: 'GMV {{amount}}' })}
                    </Typography>
                  </Box>
                  {row.isSpike ? <StatusChip tone="amber" icon={<BoltRoundedIcon />} label={t('fin_spike', { defaultValue: 'Spike' })} /> : null}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, marginInlineStart: 'auto' }}>
                    <Typography sx={{ fontSize: 13, color: c.TEXT_3, fontVariantNumeric: 'tabular-nums' }}>
                      {t('fin_vs_avg', { pct: `${row.pctVsAvg > 0 ? '+' : ''}${row.pctVsAvg.toFixed(0)}%`, defaultValue: '{{pct}} vs average' })}
                    </Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: c.INK, fontVariantNumeric: 'tabular-nums' }}>{fmt(row.commission)}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Section>
      </Box>

      <Typography variant="body2" sx={{ color: c.TEXT_3, textAlign: 'center' }}>
        {t('fin_source_note', { defaultValue: 'Figures come from /transactionStats (daily totals, kept for 5 minutes): one read per refresh, whatever the number of transactions.' })}
      </Typography>
    </Box>
  );
};

export default FinanceDashboard;
